// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as XLSX from 'xlsx'
import ExcelJS from 'exceljs';
// Style imports:
// import './HomeAlerts.scss';
// Component imports:
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import Button from 'react-bootstrap/Button';
import StickyHeaderTable from '../common/StickyHeaderTable';
import SortFilterTable from '../common/SortFilterTable';
import HomeAlertMassReviewModal from './HomeAlertMassReviewModal';
// Service imports:
import { alertStatusCompareFunction, sortTickData, tickDataCompareFunction } from '../../services/sortUtils';
import { formatDate, getUniqueTickId, turnDateToSimpleTime, _for, isReqError } from '../../services/utils';
// Action imports:
import { setSelectedStatus } from '../../redux-store/actions/alertActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import { ALERT_STATUS_DISPLAY_NAME_TO_STATUS, ALERT_STATUS_TO_DISPLAY_NAME, AlertTickData } from '../../types/api/AlertTypes';
import AlertReviewModal from './AlertReviewModal';

interface IOwnProps {
    allowStatusFilter?: boolean,
    allowFiltering?: boolean,
    dayRange: {
        fromDate: string,
        toDate: string
    } | undefined,
    currentTradingDay: string | undefined,
    showDateInTimestamp: boolean,
    hideScrollbarInTable: boolean
}
type ReduxProps = ConnectedProps<typeof connector>;
type Props = IOwnProps & ReduxProps;

/**
 *  The List of all Alerts find it **Alerts in Navbar**
 **/
const HomeAlerts: React.FC<Props> = ({
    allowStatusFilter,
    allowFiltering,
    dayRange,
    currentTradingDay,
    showDateInTimestamp,
    hideScrollbarInTable,
    selectedStatus,
    alertsByDate,
    setSelectedStatus
}) => {
    const [ singleReviewAlert, setSingleReviewAlert ] = useState<AlertTickData | null>(null);
    const [ showMassReviewModal, setShowMassReviewModal ] = useState<boolean>(false);
    const [ massReviewAlerts, setMassReviewAlerts ] = useState<AlertTickData[]>([]);
    const [ allAlerts, setAllAlerts ] = useState<AlertTickData[]>([]);

    useEffect(() => {
        setMassReviewAlerts([]);
    }, [ selectedStatus ]);

    useEffect(() => {
        // Flatten all alerts from alertsByDate into a single array for duplicate checking
        const all: AlertTickData[] = [];
        Object.values(alertsByDate).forEach(alerts => {
            if (Array.isArray(alerts)) {
                all.push(...alerts);
            }
        });
        setAllAlerts(all);
    }, [alertsByDate]);

    const { alertsToDisplay, isError, isLoading } = useMemo(() => {
        const alertsToDisplay: Array<AlertTickData> = [];
        let isLoading = false;
        let isError = false;
    
        // Single Day.
        if (currentTradingDay !== undefined) {
            const alerts = alertsByDate[currentTradingDay];
            if (alerts === undefined || alerts === 'waiting') {
                isLoading = true;
            } else if (isReqError(alerts)) {
                isError = true;
            } else {
                alertsToDisplay.push(...alerts);
            }
        }
        // Day range.
        else if (dayRange !== undefined) {
            const { fromDate, toDate } = dayRange;
            _for(fromDate, toDate, (currentDate) => {
                const alerts = alertsByDate[currentDate];
                if (alerts === undefined || alerts === 'waiting') {
                    isLoading = true;
                    return 'break';
                }
                else if (isReqError(alerts)) {
                    isError = true;
                    return 'break';
                } else {
                    alertsToDisplay.push(...alerts);
                }
            });
        }
    
        const alertsWithStatusFilterApplied = (!allowStatusFilter || selectedStatus === null)
            ? alertsToDisplay
            : alertsToDisplay.filter(alert => selectedStatus === (alert.status || 'forreview'));
    
        const sortedAlertsWithStatusFilterApplied = sortTickData(alertsWithStatusFilterApplied);
        return {
            alertsToDisplay: sortedAlertsWithStatusFilterApplied,
            isLoading,
            isError
        };
    }, [ currentTradingDay, dayRange, alertsByDate, allowStatusFilter, selectedStatus ]);


    // ============================ Export to Excel ============================
    // ---------------------------- Make Valid Data ----------------------------
    const exportToExcelMakeValidData = (colIndex: number, value: any) => {
        if (colIndex === 2) {
            const isNumber = typeof value === 'number';
            if (isNumber) {
                // Convert Excel date to JavaScript date
                const date = new Date(Math.round((value - (25567 + 2))*86400*1000));
                date.setSeconds(date.getSeconds() + 8);
            
                const day = date.getDate();
                const month = date.getMonth() + 1; // Months are zero-based in JavaScript
                const year = date.getFullYear();
                const hours = date.getHours();
                const minutes = date.getMinutes();
                const seconds = date.getSeconds();
            
                value = `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
            }
        } else if (colIndex === 6) {
            // Check if value is a status
            if (value === '') {
                value = 'forreview';
            }
            const status = ALERT_STATUS_TO_DISPLAY_NAME[value as keyof typeof ALERT_STATUS_TO_DISPLAY_NAME];
            if (status) {
                value = status;
            }
        }
        return value;
    }
    // ---------------------------- Make Valid Rows ----------------------------
    const makeValidRows = (row: any[]) => {
        for (let i = 0; i < 8; i++) {
            if (row[i] === undefined) {
                row[i] = '';
            }
        }
        return row;
    }
    // ---------------------------- Export to Excel ----------------------------
    const exportToExcel = () => {
        const table = document.querySelector('#HomeAlerts table');
        const ws = XLSX.utils.table_to_sheet(table);
        let data: any[] = XLSX.utils.sheet_to_json(ws, {header:1, raw: true});
        
        // Delete columns in table with index 3 and 4
        data.forEach((row: any[]) => {
            row.splice(4, 2);
        });

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Alerts');
    
        // Add data to worksheet
        data.forEach((row: any[], rowIndex) => {
            // Fill in empty cells with empty strings
            row = makeValidRows(row);
            row.forEach((value: any, colIndex) => {
                // Check if value is a date
                value = exportToExcelMakeValidData(colIndex, value);
        
                const cell = worksheet.getCell(rowIndex + 1, colIndex + 1);
                cell.value = value;
        
                // Make headers bold
                if (rowIndex === 0) {
                    cell.font = { bold: true };
                }
            });
        });
    
        // Adjust column widths
        worksheet.columns.forEach(column => {
            let maxColumnLength = 0;
            if (column && column.eachCell) {
                column.eachCell({ includeEmpty: true }, cell => {
                    const columnLength = cell.text.length;
                    if (columnLength > maxColumnLength) {
                        maxColumnLength = columnLength;
                    }
                });
            }
            column.width = maxColumnLength < 10 ? 10 : maxColumnLength;
        });

        // Shift data to start from the first column
        worksheet.getColumn(1).hidden = true;
        worksheet.getColumn(2).hidden = false;
        worksheet.getColumn(3).hidden = false;
        worksheet.getColumn(4).hidden = false;
        worksheet.getColumn(5).hidden = false;
        worksheet.getColumn(6).hidden = false;
        worksheet.getColumn(7).hidden = false;
        worksheet.getColumn(8).hidden = false;
        worksheet.getColumn(9).hidden = false;
        
        // Write to file
        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `alerts.xlsx`;
            a.click();
        });
    }

    // ============================ Render Functions ============================
    // --------------- Render Check Box Body ---------------
    const RenderCheckBoxBody = ({ alert, isAlertInArray, alertIndex }: { alert: AlertTickData; isAlertInArray: boolean; alertIndex: number; }) => {
        return (
            <td
                onClick={e => {
                    e.stopPropagation();
                    if (isAlertInArray) {
                        // Remove the alert.
                        setMassReviewAlerts([
                            ...massReviewAlerts.slice(0, alertIndex),
                            ...massReviewAlerts.slice(alertIndex+1)
                        ]);
                    } else {
                        // Add the alert.
                        setMassReviewAlerts(massReviewAlerts.concat([alert]));
                    }
                }}
            >
                <Form.Control
                    type='checkbox'
                    checked={isAlertInArray}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    onChange={() => {
                        if (isAlertInArray) {
                            // Remove the alert.
                            setMassReviewAlerts([
                                ...massReviewAlerts.slice(0, alertIndex),
                                ...massReviewAlerts.slice(alertIndex+1)
                            ]);
                        } else {
                            // Add the alert.
                            setMassReviewAlerts(massReviewAlerts.concat([alert]));
                        }
                    }}
                />
            </td>
        )
    }
    // --------------- Render Symbol Body ---------------
    const RenderSymbolBody = (alert: AlertTickData) => {
        return (
            <a
                href={
                    (alert.ref_timestamp !== null)
                    ? `/sigti/${alert.symbol}?tradingDay=${turnDateToSimpleTime(new Date(alert.ref_timestamp))}#${getUniqueTickId(alert)}`
                    : undefined
                }
            >
                {alert.symbol}
            </a>
        )
    }
    // --------------- Render Status Body ---------------
    const RenderStatusBody = ({ status }: { status: string | null }) => {
        return (
            <div
                className={`AlertStatusCircle StatusColorOnly ${status || 'forreview'}`}
            />
        );
    };

    const RenderReviewButton = ({ alert }: { alert: AlertTickData }) => {
        return (
            <td
                onClick={e => {
                    e.stopPropagation();
                    setSingleReviewAlert(alert);
                }}
            >
                <Button
                    size='sm'
                    variant='dark'
                    className='VerySmallButton'
                >
                    Review
                </Button>
            </td>
        )
    }

    return (
        <div id='HomeAlerts'>
            <div id='HomeAlertsHead'>
                <h5 className='SectionTitle'>Alerts</h5>
                <div id='buttonDiv'>
                    <button id='excelButton' onClick={exportToExcel} className='VerySmallButton btn btn-dark btn-sm'>Excel</button>
                </div>
            </div>
            <div className='TableWrapper'>
                <StickyHeaderTable
                    hideScrollbar={hideScrollbarInTable}
                >
                    <SortFilterTable
                        data={alertsToDisplay}
                        isLoading={isLoading}
                        isError={isError}
                        columns={[{
                            title: (
                                <div className='ExtraControlsWrapper'>
                                    {/* Select all function needs fixing */}
                                    {/* <Form.Control
                                        type='checkbox'
                                        checked={
                                            (alertsToDisplay.length === massReviewAlerts.length)
                                            && (massReviewAlerts.length !== 0)
                                        }
                                        onChange={() => {
                                            if (alertsToDisplay.length === massReviewAlerts.length) {
                                                // Remove the alert.
                                                setMassReviewAlerts([]);
                                            } else {
                                                // Add the alert.
                                                setMassReviewAlerts(alertsToDisplay);
                                            }
                                        }}
                                    /> */}
                                    {
                                        (massReviewAlerts.length !== 0)
                                        ? (
                                            <Dropdown
                                                className='HomeAlertsDropdown'
                                            >
                                                <Dropdown.Toggle
                                                    id='HomeAlertsDropdownToggle'
                                                    as='div'
                                                >
                                                    <FontAwesomeIcon
                                                        className='DownArrowIcon'
                                                        icon={faChevronDown}
                                                    />
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className='HomeAlertsDrodownMenu'>
                                                    {/* Select all function needs fixing */}
                                                    {/* <Dropdown.Item
                                                        onClick={() => setMassReviewAlerts(alertsToDisplay)}
                                                    >
                                                        Select all
                                                    </Dropdown.Item> */}
                                                    <Dropdown.Item
                                                        onClick={() => setMassReviewAlerts([])}
                                                    >
                                                        Select none
                                                    </Dropdown.Item>
                                                    <Dropdown.Item
                                                        disabled={(massReviewAlerts.length === 0 || showMassReviewModal)}
                                                        onClick={() => {setShowMassReviewModal(true)}}
                                                    >
                                                        Mass review
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        ) : null
                                    }
                                    
                                </div>
                            ),
                            overrideTd: true,
                            dataAccessor: (alert) => {
                                const alertIndex = massReviewAlerts.indexOf(alert)
                                const isAlertInArray = (alertIndex !== -1);
                                return (
                                    <RenderCheckBoxBody
                                        alert={alert}
                                        isAlertInArray={isAlertInArray}
                                        alertIndex={alertIndex}
                                    />
                                )
                            },
                        }, {
                            title: 'Symbol',
                            textAlign: 'left',
                            dataAccessor: (alert) => <RenderSymbolBody {...alert} />,
                            simpleSortable: ({ symbol }) => symbol,
                            sortTiebreaker: tickDataCompareFunction,
                            filter: (allowFiltering) ? {
                                id: 'Symbol',
                                type: 'string',
                                stringValue: ({ symbol }) => symbol
                            } : undefined
                        }, {
                            title: 'Timestamp',
                            textAlign: 'left',
                            dataAccessor: ({ref_timestamp}) => formatDate(ref_timestamp, !showDateInTimestamp),
                            sortable: tickDataCompareFunction,
                            filter: (allowFiltering) ? {
                                id: 'Timestamp',
                                type: 'string',
                                stringValue: ({ ref_timestamp }) => formatDate(ref_timestamp, !showDateInTimestamp)
                            } : undefined
                        }, {
                            title: 'Alert',
                            textAlign: 'left',
                            dataAccessor: ({description}) => description,
                            simpleSortable: ({ description }) => description,
                            sortTiebreaker: tickDataCompareFunction,
                            filter: (allowFiltering) ? {
                                id: 'Alert',
                                type: 'string',
                                stringValue: ({ description }) => description
                            } : undefined
                        }, {
                            title: 'Status',
                            textAlign: 'center',
                            dataAccessor: ({ status }) => <RenderStatusBody status={status} />,
                            sortable: (a, b) => alertStatusCompareFunction(a.status, b.status),
                            sortTiebreaker: tickDataCompareFunction,
                            filter: (allowFiltering) ? {
                                id: 'Status',
                                type: ['For Review', 'Open', 'Watch', 'Investigating', 'Critical', 'Explained', 'Closed'],
                                stringValue: ({ status }) => {
                                    return ALERT_STATUS_TO_DISPLAY_NAME[status || 'forreview'];
                                },
                                forcedValue: (selectedStatus === null) ? 'All' : ALERT_STATUS_TO_DISPLAY_NAME[selectedStatus],
                                onChange: (value) => {
                                    if (value === 'All') setSelectedStatus(null);
                                    else setSelectedStatus(ALERT_STATUS_DISPLAY_NAME_TO_STATUS[value as keyof typeof ALERT_STATUS_DISPLAY_NAME_TO_STATUS]);
                                }
                            } : undefined
                        }, {
                            title: 'Review',
                            textAlign: 'center',
                            overrideTd: true,
                            dataAccessor: (alert) => <RenderReviewButton alert={alert} />,
                        }, {
                            title: 'Latest reviewer',
                            textAlign: 'left',
                            dataAccessor: ({adu_username}) => adu_username,
                            simpleSortable: ({ adu_username }) => adu_username,
                            sortTiebreaker: tickDataCompareFunction,
                            filter: (allowFiltering) ? {
                                id: 'Latest reviewer',
                                type: 'string',
                                stringValue: ({ adu_username }) => adu_username
                            } : undefined
                        }, {
                            title: 'Private Client ID',
                            textAlign: 'left',
                            cellClassName: 'PrivateOrderClientID',
                            dataAccessor: ({private_client_id}) => private_client_id
                        }, {
                            title: 'Alert status',
                            textAlign: 'left',
                            cellClassName: 'AlertStatus',
                            dataAccessor: ({status}) => status
                        }, {
                            title: 'Alert comment',
                            textAlign: 'left',
                            cellClassName: 'AlertComment',
                            dataAccessor: ({alert_comment}) => alert_comment
                        }]}
                    />
                </StickyHeaderTable>
            </div>
            {/* Popup Modals */}
            {
                (singleReviewAlert !== null)
                ? (
                    <AlertReviewModal
                        alert={singleReviewAlert}
                        allAlerts={allAlerts}
                        onHide={() => setSingleReviewAlert(null)}
                    />
                ) : null
            }
            {/* This model is only visible if you slect alerts and click Mass Review */}
            <HomeAlertMassReviewModal
                show={showMassReviewModal}
                alerts={massReviewAlerts}
                onClear={() => setMassReviewAlerts([])}
                onHide={() => setShowMassReviewModal(false)}
            />
        </div>
    );
}

function mapStateToProps({ alertReducer }: RootState) {
    const { alertsByDate, selectedStatus } = alertReducer;
    return {
        alertsByDate,
        selectedStatus
    }
}

const connector = connect(mapStateToProps, { setSelectedStatus });
export default connector(HomeAlerts);